import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Card,
  CardBody,
  CardHeader,
  Col,
  Container,
  Row,
  Label,
  Input,
  Spinner,
  Nav,
  NavItem,
  NavLink,
  ListGroupItem,
  ListGroup,
} from "reactstrap";
import { Formik, Form, Field, ErrorMessage } from "formik";
import * as Yup from "yup";

import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import usePersistStoreData from "../../../Components/Hooks/usePersistStoreData";
import CommonLoading from "../../../Components/Common/CommonLoading";
import {
  
  reKyc_Esign_DocumentDownloadThunk,
} from "../../../slices/thunks";
import { add_incomeThunk } from "../../../slices/EditProfile/AddIncome/thunk";
import { showToast } from "../../../Components/ToastNotification";

const AnnualIncome = () => {
  const navigate = useNavigate();

  const dispatch = useDispatch();
  const { userData } = usePersistStoreData();
  const token = userData?.token;
  const [loading, setLoading] = useState(false);
  const [selectedIncome, setSelectedIncome] = useState(
    userData?.trade?.annual_income_bracket || ""
  );
  const { addInComeLoading } = useSelector((state) => state.Add_Annual_Income);
  const { reKyc_Esign_DocumentLoading } = useSelector(
    (state) => state.Re_Kyc_Esign_Document_Download
  );
  const [isProcessing, setIsProcessing] = useState(false);

  const optionIncome = [
    { key: 1, label: "Below 1 lakh" },
    { key: 2, label: "1 - 5 lakhs" },
    { key: 3, label: "5 - 10 lakhs" },
    { key: 5, label: "10 - 25 lakhs" },
    { key: 6, label: "25 lakhs - 1 Crore" },
    { key: 7, label: "More than 1 Crore" },
  ];
  const optionUploadType = [
    { key: 1, label: "Bank Statement" },
    { key: 2, label: "Demat Holding Statement" },
    { key: 3, label: "ITR Acknowledgement" },
    { key: 5, label: "Networth Certificate" },
    { key: 6, label: "Others" },
  ];

  const income_instructions = [
    "Latest 6 Months Bank Statement",
    "Latest Demat Holding Statement",
    "Latest ITR Acknowledgement",
    "Form 16 or Recent Salary Slip (for salaried employees)",
    "Net Worth Certificate from a Chartered Accountant",
    "Any Additional Certificate or Document Demonstrating Financial Net Worth",
  ];

  const initialValues = {
    annualIncome: userData?.trade?.annual_income_bracket || "",
    uploadType: "",
    incomeProof: null,
    uploadProofName: "",
  };
  const SUPPORTED_FORMATS = ["image/jpeg", "image/jpg", "image/png"];

  const validationSchema = Yup.object({
    annualIncome: Yup.string().required("Please select annual income"),
    uploadType: Yup.string().required("Please select upload type"),
    incomeProof: Yup.mixed()
      .required("Please upload income proof")
      .test("fileSize", "File size must be less than 2MB", (value) => {
        return value && value.size <= 2 * 1024 * 1024;
      })
      .test("fileFormat", "Only JPG, JPEG, PNG files are allowed", (value) => {
        return value && SUPPORTED_FORMATS.includes(value.type);
      }),
    uploadProofName: Yup.string().when("uploadType", {
      is: (val) => val == "6",
      then: (schema) => schema.required("Please enter upload proof name"),
      otherwise: (schema) => schema.notRequired(),
    }),
  });

  const onSubmit = (values, { resetForm }) => {
    handleAddIncome(values);
    // resetForm();
  };

  // Open Digio popup with API response data
  const openDigioPopup = (digioData) => {
    if (!window.Digio) {
      // console.error("Digio SDK not loaded");
      showToast(
        "Digio SDK is not loaded. Please refresh the page and try again.",
        "error"
      );
      setIsProcessing(false);
      return;
    }
    try {
      // Digio configuration with callback
      const options = {
        environment: "production",
        callback: function (response) {
          if (response.hasOwnProperty("error_code")) {
            // console.log("Error occurred in process:", response);
            // Handle error
            setIsProcessing(false);
            showToast(response?.message, "error");
            return;
          }
          showToast("Signing completed successfully", "success");
          // console.log("Signing completed successfully:", response);
          dispatch(
            reKyc_Esign_DocumentDownloadThunk({
              params: { type: "income" },
              token,
            })
          )
            .unwrap()
            .then((result) => {
              const blob = new Blob([result], { type: "application/pdf" });

              const fileURL = window.URL.createObjectURL(blob);

              // Open in new tab
              window.open(fileURL, "_blank");

              // Also download
              const link = document.createElement("a");
              link.href = fileURL;
              link.download = "income.pdf"; // file name
              document.body.appendChild(link);
              link.click();
              document.body.removeChild(link);

              // showToast("Downloaded successfully", "success");

              navigate("/profile");
              setIsProcessing(false);
              
            })
            .catch((error) => {
              showToast(
                error?.message ||
                  "Something went wrong. Please try again later",
                "error"
              );
              setIsProcessing(false);
            })

            .finally(() => {
              setIsProcessing(false);
            });
        },
        event_listener: function () {
          // Prevent Digio SDK from throwing when trying to call this
          // console.log("Digio event listener triggered");
          setIsProcessing(false);
        },
      };

      // Create new Digio instance
      const digio = new window.Digio(options);

      // Initialize Digio
      digio.init();
      digio.submit(digioData?.id, digioData?.identifier);
    } catch (error) {
      // console.error("Error opening Digio popup:", error);
      showToast("Error opening Digio: " + error.message, "error");
      setIsProcessing(false);
    }
  };

  const handleAddIncome = (values) => {
    const formData = new FormData();
    formData.append("annual_income", values?.annualIncome);
    formData.append(
      "bo_id",
      `${userData?.trade?.dp_id}${userData?.trade?.dp_account_number}`
    );
    formData.append("income_proof", values?.incomeProof);
    formData.append("upload_type", values?.uploadType);
    if (values?.uploadType == "6") {
      formData.append("upload_proof_name", values?.uploadProofName);
    }
    dispatch(add_incomeThunk({ body: formData, token }))
      .then((res) => {
        if (res?.payload?.code === 200) {
          showToast(res?.payload?.message, "success");
          openDigioPopup(res?.payload?.data);
        } else {
          showToast(res?.error?.message || "Something went wrong, please try again later", "error");
        }
      })
      .catch((err) => {
        showToast(err || "Something went wrong, please try again later", "error");
      });
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          {loading ? (
            <div
              className="centerContent h-100 w-100"
              style={{ minHeight: "80vh" }}
            >
              <CommonLoading />
            </div>
          ) : (
            <>
              <div className="position-relative mx-n4 mt-n4">
                <div
                  className="profile-wid-bg profile-setting-img"
                  style={{ backgroundColor: "#01256D" }}
                >
                  <div className="overlay-content">
                    <div className="text-end p-3">
                      <div className="p-0 ps-2 ms-auto rounded-circle profile-photo-edit">
                        <Nav
                          pills
                          className=" animation-nav profile-nav gap-2 gap-lg-3 flex-grow-1"
                          role="tablist"
                        >
                          <NavItem>
                            <NavLink
                              // className={classnames(" ", {
                              //   active: activeTab === "1",
                              // })}
                              onClick={() => {
                                navigate("/edit_option");
                              }}
                              style={{
                                backgroundColor: "transparent",
                                border: "1px solid #0FA67B",
                              }}
                            >
                              <svg
                                className="me-2"
                                width="16"
                                height="16"
                                viewBox="0 0 16 16"
                                fill="none"
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path
                                  d="M3.99 9L16 9L16 7L3.99 7L3.99 4L3.49691e-07 8L3.99 12L3.99 9Z"
                                  fill="#0FA67B"
                                />
                              </svg>
                              <span className="d-none d-md-inline-block">
                                Go Back
                              </span>
                            </NavLink>
                          </NavItem>
                        </Nav>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <Row>
                <Col xxl={12} xl={12}>
                  <Card className="mt-n5">
                    <CardHeader>
                      <h5 className="card-title mb-0">Annual Income</h5>
                    </CardHeader>
                    <CardBody>
                      <Row className="row m-0 p-0">
                        <Formik
                          initialValues={initialValues}
                          validationSchema={validationSchema}
                          onSubmit={onSubmit}
                        >
                          {({
                            values,
                            handleChange,
                            handleBlur,
                            setFieldValue,
                          }) => (
                            <Form className="px-0">
                              <Row>
                                <Col lg={6} className="mb-3">
                                  <Label
                                    htmlFor="tradingCodeInput"
                                    className="form-label"
                                  >
                                    Trading Code
                                  </Label>
                                  <Input
                                    type="text"
                                    id="tradingCodeInput"
                                    name="tradingCode"
                                    defaultValue={userData?.trade?.short_code}
                                    disabled
                                    className="form-control"
                                  />
                                </Col>
                                <Col lg={6} className="mb-3">
                                  <Label
                                    htmlFor="beneficiaryOwnerInput"
                                    className="form-label"
                                  >
                                    Beneficiary Owner (BO ID)
                                  </Label>
                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="beneficiaryOwnerInput"
                                    placeholder="Enter beneficiary owner (BO ID)"
                                    defaultValue={`${userData?.trade?.dp_id}${userData?.trade?.dp_account_number}`}
                                    disabled
                                  />
                                </Col>

                                <Col lg={6} className="mb-3">
                                  <Label htmlFor="email" className="form-label">
                                    Email ID
                                  </Label>

                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="email"
                                    placeholder="Enter your email address"
                                    defaultValue={
                                      userData?.personal_details?.email
                                    }
                                    disabled
                                  />
                                </Col>

                                <Col lg={6} className="mb-3">
                                  <Label
                                    htmlFor="firstnameInput"
                                    className="form-label"
                                  >
                                    Client Name
                                  </Label>

                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="firstnameInput"
                                    placeholder="Enter client name"
                                    defaultValue={userData?.name}
                                    disabled
                                  />
                                </Col>
                                <Col lg={6} className="mb-3">
                                  <Label
                                    htmlFor="phonenumberInput"
                                    className="form-label"
                                  >
                                    Mobile Number
                                  </Label>

                                  <Input
                                    type="text"
                                    className="form-control"
                                    id="phonenumberInput"
                                    placeholder="Enter your mobile number"
                                    defaultValue={`+91 ${userData?.personal_details?.mobile}`}
                                    disabled
                                  />
                                </Col>
                                <Col lg={6} className=" mb-3">
                                  <Label
                                    htmlFor="annualIncome"
                                    className="form-label"
                                  >
                                    Annual Income
                                  </Label>
                                  <Input
                                    type="select"
                                    id="annualIncome"
                                    name="annualIncome"
                                    value={values.annualIncome}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control"
                                  >
                                    <option value="" disabled>
                                      Select Income
                                    </option>
                                    {optionIncome?.map((item) => (
                                      <option value={item?.key} key={item?.key}>
                                        {item?.label}
                                      </option>
                                    ))}
                                  </Input>
                                  <ErrorMessage
                                    name="annualIncome"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>

                                <Col lg={6} className=" mb-3">
                                  <Label
                                    htmlFor="uploadType"
                                    className="form-label"
                                  >
                                    Annual Income Upload Type
                                  </Label>
                                  <Input
                                    type="select"
                                    id="uploadType"
                                    name="uploadType"
                                    value={values.uploadType}
                                    onChange={handleChange}
                                    onBlur={handleBlur}
                                    className="form-control"
                                  >
                                    <option value="" disabled>
                                      Select
                                    </option>
                                    {optionUploadType?.map((item) => (
                                      <option value={item?.key} key={item?.key}>
                                        {item?.label}
                                      </option>
                                    ))}
                                  </Input>
                                  <ErrorMessage
                                    name="uploadType"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>
                                {values.uploadType == "6" && (
                                  <Col lg={6} className="mb-3">
                                    <Label
                                      htmlFor="uploadProofName"
                                      className="form-label"
                                    >
                                      Enter Upload Proof Name
                                    </Label>
                                    <Input
                                      type="text"
                                      id="uploadProofName"
                                      name="uploadProofName"
                                      value={values.uploadProofName}
                                      onChange={handleChange}
                                      onBlur={handleBlur}
                                      className="form-control"
                                      placeholder="Enter proof name"
                                    />
                                    <ErrorMessage
                                      name="uploadProofName"
                                      component="div"
                                      className="text-danger mt-1"
                                    />
                                  </Col>
                                )}

                                <Col lg={6} className="mb-3">
                                  <Label
                                    htmlFor="incomeProof"
                                    className="form-label"
                                  >
                                    Upload Income Proof
                                  </Label>
                                  <Input
                                    type="file"
                                    id="incomeProof"
                                    name="incomeProof"
                                    onChange={(e) => {
                                      setFieldValue(
                                        "incomeProof",
                                        e.currentTarget.files[0]
                                      );
                                    }}
                                    onBlur={handleBlur}
                                    accept="image/jpeg, image/jpg, image/png"
                                    className="form-control"
                                  />
                                  <ErrorMessage
                                    name="incomeProof"
                                    component="div"
                                    className="text-danger mt-1"
                                  />
                                </Col>
                                <div className="ps-2 mb-3">
                                  <ListGroup flush>
                                    {income_instructions?.map(
                                      (point, index) => (
                                        <ListGroupItem
                                          key={index}
                                          className="d-flex align-items-start fs-14 fw-medium border-0 p-1 pt-0"
                                        >
                                          <i className="ri-pushpin-line text-primary me-2 fs-5 flex-shrink-0"></i>

                                          <span>{point}</span>
                                        </ListGroupItem>
                                      )
                                    )}
                                  </ListGroup>
                                </div>
                                <div className="text-end ">
                                  <button
                                    type="submit"
                                    className="btn btn-primary fs-16"
                                    style={{ minWidth: "100px" }}
                                    disabled={addInComeLoading || isProcessing ||reKyc_Esign_DocumentLoading}
                                  >
                                    {addInComeLoading || isProcessing ||reKyc_Esign_DocumentLoading ? (
                                      <Spinner size="sm" />
                                    ) : (
                                      "Proceed"
                                    )}
                                  </button>
                                </div>
                              </Row>
                            </Form>
                          )}
                        </Formik>
                      </Row>
                    </CardBody>
                  </Card>
                </Col>
              </Row>
            
            </>
          )}
        </Container>
      </div>
    </React.Fragment>
  );
};

export default AnnualIncome;
